import type Highcharts from 'highcharts';

export type AspectRatioOption =
  '16:9' | '4:3' | '3:2' | '1:1' | 'current' | 'custom';

export type ElementSize = 'small' | 'medium' | 'large' | 'xlarge';

export type OutputFormat = 'png' | 'jpeg';

export interface ElementDescriptor {
  key: string;
  label: string;
  available: boolean;
}

export interface ExportImageOptions {
  aspectRatio: AspectRatioOption;
  // null while the custom width/height field is empty or holds invalid input.
  customWidth: number | null;
  customHeight: number | null;
  elementSize: ElementSize;
  // Absent keys default to "include"; only `false` hides the element.
  elements: Record<string, boolean>;
  outputFormat: OutputFormat;
  // Title used when the `title` element toggle is on — mirrors the drawer header.
  chartLabel?: string;
}

export interface ChartImageExporter {
  getLiveSize: () => { width: number; height: number } | null;
  getElements: () => ElementDescriptor[];
  // Resolves `true` on download, `false` when there's no chart to export; rejects on render failure.
  exportAndDownload: (
    opts: ExportImageOptions,
    filename: string
  ) => Promise<boolean>;
  // Renders the chart to an image Blob WITHOUT downloading it — used by bulk/content
  // export to upload PNGs. Resolves `null` when there's no chart to capture; rejects on
  // render failure. Optional: only exporters wired for blob capture implement it (the
  // SciChart exporter does; the Highcharts exporter gains it with the Reserves slice).
  exportToBlob?: (opts: ExportImageOptions) => Promise<Blob | null>;
}

// Per-chart SciChart hooks. All optional; each apply/restore pair must round-trip.
export interface SciChartElementHooks {
  getElements?: () => ElementDescriptor[];
  applyScaling?: (factor: { fontFactor: number; userFactor: number }) => void;
  restoreScaling?: () => void;
  applyVisibility?: (elements: Record<string, boolean>) => void;
  restoreVisibility?: () => void;
}

// ─── Highcharts exporter config ───────────────────────────────────────────────

export interface HighchartsElementDescriptor {
  key: string;
  label: string;
  available: boolean;
}

// Custom drawer-key → option-mutation map. Consumer-supplied entries are merged
export type HighchartsElementHiderMap = Record<
  string,
  (cloned: Highcharts.Options) => void
>;

// Numeric/typographic defaults. Every field is optional; the exporter falls back
// to its built-in tuned-for-Wells defaults when nothing is passed.
export interface HighchartsExporterPresets {
  // Per-preset axis tick spacing (px). Higher value = fewer ticks.
  tickPixelIntervalBySize?: Record<ElementSize, number>;
  targetTickCountBySize?: Partial<Record<ElementSize, number>>;
  // Min/max clamps for per-key numeric scaling (radius, lineWidth, …).
  visualClamps?: Record<string, readonly [number, number]>;
  // Default font size applied to the export title when none is set on the chart.
  titleFontSizePx?: string;
  // Axis-title margin baselines (px). Highcharts defaults are 5 (xAxis) / 10 (yAxis).
  axisTitleMarginBase?: { x?: number; y?: number };
}

export interface HighchartsExporterFeatures {
  applySparseTicks?: boolean;
  disableHtmlDataLabels?: boolean;
  injectLabelMultiplier?: boolean;
}

// One entry for an export-only legend (for charts whose on-screen legend is a
// React component). `checked` renders a ☑/☐ prefix to mirror a toggle legend.
export interface ExportLegendItem {
  label: string;
  color: string;
  checked?: boolean;
}

export interface HighchartsExporterConfig {
  getChart: () => Highcharts.Chart | null;
  elementHooks: {
    getElements: () => HighchartsElementDescriptor[];
  };
  // Filter applied to the cloned series list. Return `false` to drop the series
  // from the exported image. Defaults to keeping every series.
  filterSeries?: (series: Highcharts.SeriesOptionsType) => boolean;
  // Extra drawer-key → hider entries to merge with the built-in vocabulary.
  extraElementHiders?: HighchartsElementHiderMap;
  presets?: HighchartsExporterPresets;
  features?: HighchartsExporterFeatures;
  // X-axis title rendered in the EXPORT only, for charts whose x-axis label lives
  // in an external switcher (so the image still shows what the x-axis is).
  xAxisTitle?: string;
  // Render an export-only Highcharts legend from these items, for charts whose
  // on-screen legend is a React component the SVG export can't capture.
  legendItems?: ExportLegendItem[];
  // Export-only footer lines rendered as the chart caption (bottom of the image), for
  // summary numbers that live OUTSIDE the chart on screen (e.g. the Cash Flow cumulative
  // table below the chart). One string per line. Absent → no caption added.
  footerLines?: string[];
  // Copy the live chart's plotLines/plotBands into the export (default: false). The exporter
  // otherwise rebuilds from `userOptions`, which omits plotlines added imperatively via
  // `axis.addPlotLine` (e.g. the Cash Flow "Eff Date" line) — so without this they vanish.
  copyPlotLines?: boolean;
}

export const LABEL_MULTIPLIER_KEY = 'labelMultiplier' as const;

export interface ChartExportSeriesCustom {
  [LABEL_MULTIPLIER_KEY]?: number;
}

// ─── SciChart exporter config ─────────────────────────────────────────────────

export interface SciChartCaptureOptions {
  // Canvas background. `null` → transparent (PNG only). Defaults to '#ffffff'.
  backgroundColor?: string | null;
  // html-to-image pixelRatio. Defaults to 1 because the surface is pre-sized.
  pixelRatio?: number;
  // html-to-image cacheBust. Defaults to true.
  cacheBust?: boolean;
  // 0–1, applied only when outputFormat === 'jpeg'. Defaults to 0.95.
  jpegQuality?: number;
  // Upper bound on the wait-for-render handshake (ms). Defaults to 2000.
  renderWaitTimeoutMs?: number;
  // When resizing the surface for capture, hide SciChart's shared WebGL canvas
  // (useSharedCache) for the capture window so its full-size repaint doesn't
  // flash on-screen. Off by default — opt in only for headless/bulk capture
  // where no freeze overlay covers that shared canvas. See `captureToBlob`.
  hideSharedCanvasOnResize?: boolean;
}
